import { useState, useEffect, useRef } from 'react';
import { VastParser }  from '../lib/VastParser';
import { VastTracker } from '../lib/VastTracker';
import { VastError }   from '../lib/VastError';

/**
 * Fetches + parses VAST. State machine: idle → loading → ready | error
 * Tracker is created without assetUri context here — the component
 * recreates it with full context once the media format is selected,
 * so [ASSETURI] resolves to the real URL in every tracking pixel.
 */
export function useVastPlayer({ vastUrl, vastXml, maxWrapperDepth = 5, timeout = 10000 }) {
  const [status,   setStatus]   = useState('idle');
  const [vastData, setVastData] = useState(null);
  const [error,    setError]    = useState(null);
  const trackerRef              = useRef(null);

  useEffect(() => {
    if (!vastUrl && !vastXml) return;
    let cancelled = false;
    setStatus('loading');
    setVastData(null);
    setError(null);
    trackerRef.current = null;

    const parser = new VastParser({ maxWrapperDepth, timeout });

    (async () => {
      try {
        const data = vastUrl
          ? await parser.fetchAndParse(vastUrl)
          : parser.parseXML(vastXml);
        if (cancelled) return;
        if (!data?.linear)                   throw new VastError(200, 'No Linear creative');
        if (!data.linear.mediaFiles?.length) throw new VastError(401, 'No MediaFiles');
        trackerRef.current = new VastTracker(data);
        setVastData(data);
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        const ve = err instanceof VastError ? err : new VastError(900, err.message);
        trackerRef.current?.fireError(ve.vastCode);
        setError(ve);
        setStatus('error');
      }
    })();

    return () => { cancelled = true; };
  }, [vastUrl, vastXml, maxWrapperDepth, timeout]); // eslint-disable-line

  return { status, vastData, error, trackerRef };
}